import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, TouchableOpacity, FlatList, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../../components/DirectionalIcon';
import { useAppContext } from '../../contexts/AppContext';
import { apiService, AssignmentItem } from '../../services/api';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const PRIORITY_COLOR: Record<string, string> = {
  low: colors.textLight as string,
  normal: colors.info as string,
  high: colors.warning as string,
  urgent: colors.error as string,
};
const STATUS_COLOR: Record<string, string> = {
  active: colors.info as string,
  inProgress: colors.warning as string,
  locked: colors.textLight as string,
  completed: colors.success as string,
  cancelled: colors.textLight as string,
  transferred: colors.textLight as string,
};

function isOverdue(a: AssignmentItem): boolean {
  return a.status === 'active' && !!a.dueDate && new Date(a.dueDate) < new Date();
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const LIMIT = 20;

/** Full assignments list reachable from the dashboard's Assignments card
 * "View all" CTA. Infinite scroll via the newly-paginated getAssignments
 * (Session 11). No row actions this phase — no assignment detail screen
 * exists anywhere in this app yet. */
export const DashboardAssignmentsAllScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo } = useAppContext();

  const [items, setItems] = useState<AssignmentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const load = useCallback(async (reset: boolean, loadMore = false) => {
    if (loadMore && (!hasMore || loadingMore)) return;

    const page = reset ? 0 : loadMore ? currentPage + 1 : currentPage;
    if (reset) setLoading(true);
    if (loadMore) setLoadingMore(true);

    try {
      const res = await apiService.getAssignments({ limit: LIMIT, offset: page * LIMIT });
      const data = res.data?.items ?? [];
      setItems((prev) => (loadMore ? [...prev, ...data] : data));
      setTotal(res.data?.total ?? 0);
      setHasMore(data.length === LIMIT);
      setCurrentPage(page);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentPage, hasMore, loadingMore]);

  useEffect(() => {
    load(true);
  }, []);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore && !loading) load(false, true);
  };

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigateTo('dashboard')} activeOpacity={0.7}>
          <DirectionalIcon name="chevron-back" size={20} color={colors.text.primary as string} />
        </TouchableOpacity>
        <View>
          <Text style={styles.headerTitle}>Assignments</Text>
          <Text style={styles.headerSubtitle}>{total} total</Text>
        </View>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.brand.primary as string} />
        </View>
      ) : (
        <FlatList
          data={items}
          keyExtractor={(item) => item._id}
          contentContainerStyle={styles.listContent}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.5}
          ListEmptyComponent={
            <View style={styles.empty}>
              <Ionicons name="clipboard-outline" size={24} color={colors.text.secondary as string} />
              <Text style={styles.emptyText}>No assignments yet</Text>
            </View>
          }
          ListFooterComponent={
            loadingMore ? (
              <View style={styles.footerLoading}>
                <ActivityIndicator size="small" color={colors.brand.primary as string} />
              </View>
            ) : null
          }
          renderItem={({ item: a }) => {
            const overdue = isOverdue(a);
            return (
              <View style={styles.row}>
                <View style={styles.rowContent}>
                  <Text style={styles.title} numberOfLines={1}>{a.title}</Text>
                  <Text style={styles.meta} numberOfLines={1}>{a.assignmentType} · {a.targetType}</Text>
                </View>
                <View style={styles.badges}>
                  <View style={[styles.badge, { backgroundColor: `${PRIORITY_COLOR[a.priority] ?? colors.textLight}20` }]}>
                    <Text style={[styles.badgeText, { color: PRIORITY_COLOR[a.priority] ?? colors.textLight }]}>
                      {a.priority}
                    </Text>
                  </View>
                  <View style={styles.statusLine}>
                    <View style={[styles.statusDot, { backgroundColor: STATUS_COLOR[a.status] ?? colors.textLight }]} />
                    <Text style={styles.statusText}>{a.status}</Text>
                  </View>
                  {a.dueDate && (
                    <Text style={[styles.dueDate, overdue && styles.dueDateOverdue]}>
                      {overdue ? 'Overdue · ' : 'Due '}{formatDate(a.dueDate)}
                    </Text>
                  )}
                </View>
              </View>
            );
          }}
        />
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background.base,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  backButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: designTokens.borderRadius.full,
    borderWidth: 1,
    borderColor: colors.border,
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  listContent: {
    padding: designTokens.spacing.md,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: designTokens.spacing.sm,
    marginBottom: designTokens.spacing.sm,
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  meta: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  badges: {
    alignItems: 'flex-end',
    gap: 4,
  },
  badge: {
    paddingHorizontal: designTokens.spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.full,
  },
  badgeText: {
    fontSize: 10,
    fontFamily: typography.fontFamily.semibold,
    textTransform: 'capitalize',
  },
  statusLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 10,
    color: colors.text.secondary,
    textTransform: 'capitalize',
  },
  dueDate: {
    fontSize: 10,
    color: colors.text.secondary,
  },
  dueDateOverdue: {
    color: colors.status.error,
    fontFamily: typography.fontFamily.semibold,
  },
  footerLoading: {
    paddingVertical: designTokens.spacing.md,
    alignItems: 'center',
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: designTokens.spacing.xs,
    paddingVertical: designTokens.spacing.lg * 2,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
});
